import { motion } from 'framer-motion'
import { 
  Thermometer, 
  Droplets, 
  Wind, 
  Trees, 
  Fish,
  Mountain,
  Users,
  Zap
} from 'lucide-react'
import { GameState } from '../types/game'

interface ParameterPanelProps {
  gameState: GameState | null
}

export function ParameterPanel({ gameState }: ParameterPanelProps) {
  if (!gameState) return null
  
  const params = gameState.parameters
  
  const parameterGroups = [
    {
      title: '大気圏',
      icon: Wind,
      color: 'text-cyan-400',
      items: [
        { label: '気温', value: `${params.atmosphere.temperature}°C`, icon: Thermometer },
        { label: '湿度', value: `${params.atmosphere.humidity}%`, icon: Droplets },
        { label: 'CO2', value: `${params.atmosphere.co2Level}%`, progress: params.atmosphere.co2Level },
        { label: '酸素', value: `${params.atmosphere.oxygenLevel}%`, progress: params.atmosphere.oxygenLevel }
      ]
    },
    {
      title: '地殻',
      icon: Mountain,
      color: 'text-amber-400',
      items: [
        { label: '地殻活動', value: `${params.geology.tectonicActivity}%`, progress: params.geology.tectonicActivity },
        { label: '火山活動', value: `${params.geology.volcanicActivity}%`, progress: params.geology.volcanicActivity },
        { label: '地震活動', value: `${params.geology.seismicActivity}%`, progress: params.geology.seismicActivity },
        { label: '標高', value: `${params.geology.landElevation}m` }
      ]
    },
    {
      title: '水圏',
      icon: Fish,
      color: 'text-blue-400',
      items: [
        { label: '水位', value: `${params.hydrosphere.waterLevel}%`, progress: params.hydrosphere.waterLevel },
        { label: '海水温', value: `${params.hydrosphere.oceanTemperature}°C` },
        { label: '氷床', value: `${params.hydrosphere.iceCoverage}%`, progress: params.hydrosphere.iceCoverage },
        { label: '河川', value: `${params.hydrosphere.riverFlow}%`, progress: params.hydrosphere.riverFlow }
      ]
    },
    {
      title: '生物圏',
      icon: Trees,
      color: 'text-green-400',
      items: [
        { label: '植物', value: `${params.biosphere.plantCoverage}%`, progress: params.biosphere.plantCoverage },
        { label: '動物', value: `${params.biosphere.animalDiversity}%`, progress: params.biosphere.animalDiversity },
        { label: '微生物', value: `${params.biosphere.microbialActivity}%`, progress: params.biosphere.microbialActivity },
        { label: 'バイオマス', value: `${params.biosphere.biomass}t` }
      ]
    },
    {
      title: '文明',
      icon: Users,
      color: 'text-purple-400',
      items: [
        { label: '人口', value: params.civilization.population.toLocaleString() },
        { label: '技術', value: `${params.civilization.technologyLevel}%`, progress: params.civilization.technologyLevel },
        { label: '汚染', value: `${params.civilization.pollution}%`, progress: params.civilization.pollution },
        { label: '消費', value: `${params.civilization.resourceConsumption}%`, progress: params.civilization.resourceConsumption }
      ]
    }
  ]
  
  return (
    <div className="glass-panel p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-cyan-300">世界の状態</h2>
        <div className="text-sm">
          <span className="text-gray-400">年齢:</span>
          <span className="ml-2 font-mono">{gameState.worldAge.toLocaleString()}年</span>
        </div>
      </div>
      
      <div className="space-y-4">
        {parameterGroups.map((group) => {
          const Icon = group.icon
          
          return (
            <div key={group.title} className="space-y-2">
              <div className={`flex items-center space-x-2 ${group.color}`}>
                <Icon className="w-4 h-4" />
                <span className="text-sm font-semibold">{group.title}</span>
              </div>
              
              <div className="grid grid-cols-2 gap-2 text-xs">
                {group.items.map((item) => (
                  <div key={item.label} className="space-y-1">
                    <div className="flex justify-between text-gray-300">
                      <span>{item.label}</span>
                      <span className="font-mono">{item.value}</span>
                    </div>
                    
                    {item.progress !== undefined && (
                      <div className="h-1 bg-gray-700 rounded-full overflow-hidden">
                        <motion.div
                          className={`h-full ${
                            item.progress > 70 ? 'bg-red-500' :
                            item.progress > 40 ? 'bg-yellow-500' :
                            'bg-green-500'
                          }`}
                          initial={{ width: 0 }}
                          animate={{ width: `${item.progress}%` }}
                          transition={{ duration: 0.5 }}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )
        })}
      </div>
      
      {/* 世界の健康状態 */}
      <div className="mt-4 p-3 rounded-lg bg-black/30">
        <div className="flex items-center justify-between">
          <span className="text-sm">世界の状態</span>
          <span className={`text-sm font-bold ${
            gameState.isAlive ? 'text-green-400' : 'text-red-400'
          }`}>
            {gameState.isAlive ? '✨ 生きている' : '💀 滅亡'}
          </span>
        </div>
      </div>
    </div>
  )
}